<template>
	<v-card
		class="mx-auto"
	>
		<v-card-title>
			<translate>
				Change Pricing Parameters
			</translate>
			<v-spacer />
			<v-progress-circular
				v-if="loading"
				:size="25"
				indeterminate
				color="primary"
			/>
		</v-card-title>
		<v-card-text>
			<v-form
				ref="form"
				v-model="valid"
			>
				<v-row>
					<v-col>
						<tooltip-field
							v-model="alpha"
							:rules="nonEmptyRules"
							label="Alpha"
							tooltip-text="A fixed parameter from the heuristic, relating to the damage part of claims cost"
							type="number"
							variant="outlined"
							:hide-details="valid || unchanged"
							:disabled="loading"
						/>
					</v-col>
					<v-col>
						<tooltip-field
							v-model="beta"
							:rules="nonEmptyRules"
							label="Beta"
							tooltip-text="A fixed parameter from the heuristic, relating to the loss part of claims cost"
							type="number"
							variant="outlined"
							:hide-details="valid || unchanged"
							:disabled="loading"
						/>
					</v-col>
					<v-col>
						<tooltip-field
							v-model="sigma"
							:rules="nonEmptyRules.concat([
								(v) => Number.isInteger(Number(v)) || 'Number must be an integer'
							])"
							label="Sigma"
							tooltip-text="The threshold minimum number of policy days for a device series or brand"
							type="number"
							variant="outlined"
							:hide-details="valid || unchanged"
							:disabled="loading"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-textarea
							v-model="comment"
							:label="$gettext('Comment')"
							:disabled="loading"
							variant="outlined"
						/>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-spacer />
			<v-btn
				:disabled="!valid || loading || unchanged"
				variant="text"
				color="primary"
				@click="submit"
			>
				<translate>
					Submit
				</translate>
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script>
import TooltipField from '../atoms/TooltipField.vue';
import {
	axios,
} from '../../services/axios-service';
export default {
	name: 'PricingParameterForm',

	components: {
		TooltipField,
	},

	props: {
		productVersionId: {
			type: Number,
			required: true
		}
	},

	emits: ['update',],

	data () {
		return {
			alpha: null,
			beta: null,
			sigma: null,
			memory: null,
			comment: '',
			valid: false,
			loading: false,
		};
	},

	computed: {
		unchanged () {
			if (this.memory === null) {
				return true;
			}
			return (this.memory.alpha === this.alpha && this.memory.beta === this.beta && this.memory.sigma === this.sigma);
		},

		/**
		 * @returns {Array} Checks if fields are valid
		 */
		nonEmptyRules () {
			return [
				(x) => !!x || this.$gettext('Required!'),
				(v) => !isNaN(v) || 'Must be a number',
				(v) => Number(v) >= 0 || 'Number must be positive',
				(v) => Number(v) !== 0 || 'Number can\'t be 0',
			];
		}
	},

	mounted () {
		this.getCurrent();
	},

	methods: {
		/**
		 * get latest model parameters
		 *
		 * @returns {void}
		 */
		getCurrent () {
			this.loading = true;
			axios.get(`/api/v0/productpricingparameters?productversion_id=${this.productVersionId}`).then(({
				data
			}) => {
				if(data.length) {
					const latest = data[data.length - 1];

					this.alpha = String(latest.alpha);
					this.beta = String(latest.beta);
					this.sigma = String(latest.sigma);
					this.memory = {
						alpha: this.alpha,
						beta: this.beta,
						sigma: this.sigma,
					};
				} else {
					this.memory = {
						alpha: null,
						beta: null,
						sigma: null,
					};
				}
			}).finally(() => {
				this.loading = false;
			});
		},

		/**
		 * Submit model parameters
		 *
		 * @returns {void}
		 */
		submit () {
			this.loading = true;
			const data = new FormData();

			data.set('alpha', this.alpha);
			data.set('beta', this.beta);
			data.set('sigma', this.sigma);
			data.set('productVersionId', this.productVersionId.toString());

			if (this.comment.length > 0) {
				data.set('comment', this.comment);
			}

			axios.post('/bo/pricing/parameters', data).then(() => {
				this.$emit('update');
				this.$refs.form.reset();
			}).catch((error) => {
				window.captureException(error);
			}).then(() => {
				this.loading = false;
			});
		},
	},
};
</script>
